import { useRef, useCallback, MutableRefObject } from "react";
import { Object, TransformState } from "./useArtHistory";
import { KonvaArtStageRefs } from "../components/KonvaCanvas";

type SerializeThenSaveFunction = (scenario?: string, objectsProp?: any[]) => void;

interface UseTransformProps {
  objects: Object[];
  setObjects: React.Dispatch<React.SetStateAction<Object[]>>;
  stageRef: React.RefObject<KonvaArtStageRefs>;
  panelSize: number;
  imageSize: number;
  serializeThenSaveRef: MutableRefObject<SerializeThenSaveFunction | null>;
}

export const useTransform = ({
  objects,
  setObjects,
  stageRef,
  panelSize,
  imageSize,
  serializeThenSaveRef,
}: UseTransformProps) => {
  const isTransformingRef = useRef<boolean>(false);

  // Capture transformations before saving to history
  const captureTransformations = useCallback(() => {
    const drawStage = stageRef.current?.drawStageRef.current;
    const drawLayer = stageRef.current?.drawLayerRef.current;
    
    if (!drawStage || !drawLayer) {
      console.warn('Cannot capture transformations - stage ref is null');
      return objects;
    }
    
    // If objects array is empty, return empty array
    if (objects.length === 0) {
      return [];
    }
    
    // Calculate the scale factor based on the reference size
    const REFERENCE_SIZE = imageSize;
    const scaleFactor = panelSize / REFERENCE_SIZE;
    
    // Create a copy of objects with transformation data
    const transformedObjects = objects.map((object, index) => {
      try {
        const node = drawLayer.findOne(`#object-${index}`);
        
        // If node doesn't exist, use the transform data from the object itself
        if (!node) {
          return {
            ...object,
            transform: object.transform || {
              x: 0,
              y: 0,
              rotation: 0,
              scaleX: 1,
              scaleY: 1
            }
          };
        }
        
        // Get the raw transform values
        const rawTransform = {
          x: node.x(),
          y: node.y(),
          rotation: node.rotation(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY()
        };
        
        // Create a new transform object from the node, adjusting for reference size
        const transform: TransformState = {
          x: node.x() / scaleFactor,
          y: node.y() / scaleFactor,
          rotation: node.rotation(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY()
        };
        
        return {
          ...object,
          transform
        };
      } catch (error) {
        console.error(`Error capturing transformation for object-${index}:`, error);
        // Return the object with its original transform as fallback
        return object;
      }
    });
    return transformedObjects;
  }, [objects, stageRef, panelSize, imageSize]);

  // Helper function to safely call serializeThenSave
  const safeSerialize = useCallback((scenario?: string, objectsProp?: any[]) => {
    if (serializeThenSaveRef.current) {
      serializeThenSaveRef.current(scenario, objectsProp);
    } else {
      console.warn('serializeThenSave function not available');
    }
  }, [serializeThenSaveRef]);

  // Handle transform end (move, resize, rotate)
  const handleTransformEnd = useCallback((transformState: TransformState, strokeIndex: number) => {
    // Keep transformation flag true until after we save to history to prevent duplicate history entries
    
    if (strokeIndex >= 0 && strokeIndex < objects.length) {
      // Update the specific stroke with the new transform state
      setObjects(prevStrokes => {
        const newStrokes = [...prevStrokes];
        if (newStrokes[strokeIndex]) {
          newStrokes[strokeIndex] = {
            ...newStrokes[strokeIndex],
            transform: transformState
          };
        }
        return newStrokes;
      });
    
      // Save to history after a short delay to ensure state is updated
      setTimeout(() => {
        safeSerialize('after transform');
        // Only mark transformation as complete after saving to history
        isTransformingRef.current = false;
      }, 50);
    } else {
      // If no specific stroke index, just save the current state
      safeSerialize('after transform');
      // Only mark transformation as complete after saving to history
      isTransformingRef.current = false;
    }
  }, [objects, safeSerialize, setObjects]);

  const handleImageAdd = useCallback((dataUrl: string, transformState?: TransformState) => {
    // Use provided transform state or create a default one
    const defaultTransform: TransformState = transformState || {
      x: 0,
      y: 0,
      rotation: 0,
      scaleX: 1,
      scaleY: 1
    };
    
    // Create a new image object
    const imageObject: Object = {
      type: 'image',
      data: {
        points: [], // Empty points array for image objects
        thickness: 0, // Not used for images
        color: '', // Not used for images
        imageData: dataUrl
      },
      transform: defaultTransform
    };
    
    // Add the image to the objects array and ensure it's included in history
    setObjects(prev => {
      const newObjects = [...prev, imageObject];
      
      // Use a longer timeout to ensure the Konva stage has time to render the image before capturing transformations and saving to history
      setTimeout(() => {
        safeSerialize('after image add', newObjects);
      }, 200);
      
      return newObjects;
    });
  }, [safeSerialize, setObjects]);

  const handleTransformStart = useCallback(() => {
    isTransformingRef.current = true;
  }, []);

  return {
    captureTransformations,
    handleTransformEnd,
    handleImageAdd,
    handleTransformStart,
    isTransformingRef
  };
};
